import { Input, Textarea } from "@design";
import { useId } from "react";
import { useTranslation } from "react-i18next";
import { Text } from "@/components/Text";
import type { AskUserQuestion } from "./AskUserCard";

/** The HTML input types the `ask_user` tool can ask for (`input_type`); anything else is plain text. */
const INPUT_TYPES = new Set(["text", "number", "date", "email", "url"]);

/** One "@", no spaces, and a dot inside the domain. String checks rather than a regex (no backtracking). */
function isEmail(value: string) {
  const at = value.indexOf("@");
  if (at < 1 || at !== value.lastIndexOf("@") || /\s/.test(value)) return false;
  const domain = value.slice(at + 1);
  const dot = domain.lastIndexOf(".");
  return dot > 0 && dot < domain.length - 1;
}

function isLink(value: string) {
  try {
    const url = new URL(value);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
}

/** Why a typed answer isn't valid for its `input_type` (an i18n key under `askUser.errors`), or null. */
export function answerError(question: AskUserQuestion, value: string): "email" | "url" | "number" | null {
  const text = value.trim();
  if (!text) return null;
  if (question.input_type === "email" && !isEmail(text)) return "email";
  if (question.input_type === "url" && !isLink(text)) return "url";
  if (question.input_type === "number" && Number.isNaN(Number(text))) return "number";
  return null;
}

/** The input type and placeholder for a question's free-text answer. */
function fieldShape(question: AskUserQuestion, placeholders: { text: string; email: string; url: string }) {
  const type = question.input_type && INPUT_TYPES.has(question.input_type) ? question.input_type : "text";
  const byType: Record<string, string | undefined> = {
    email: placeholders.email,
    url: placeholders.url,
    date: undefined,
  };
  return { type, placeholder: type in byType ? byType[type] : placeholders.text };
}

/**
 * One question's free-text answer, shaped by its `input_type` (multi-line, number, date,
 * email, link). Format errors show inline once `showError` is on, not as the browser's bubble.
 */
export function AskUserAnswerField({
  question,
  hasOptions,
  value,
  showError,
  onChange,
  onBlur,
}: Readonly<{
  question: AskUserQuestion;
  hasOptions: boolean;
  value: string;
  showError: boolean;
  onChange: (value: string) => void;
  onBlur: () => void;
}>) {
  const { t } = useTranslation("chat", { keyPrefix: "askUser" });
  const errorId = useId();
  const errorKey = showError ? answerError(question, value) : null;
  const common = {
    "aria-label": t("answer", { question: question.question }),
    value,
    onChange: (event: { target: { value: string } }) => onChange(event.target.value),
    onBlur,
  };
  const textPlaceholder = hasOptions ? t("other") : t("placeholder");
  const { type, placeholder } = fieldShape(question, {
    text: textPlaceholder,
    email: t("placeholders.email"),
    url: t("placeholders.url"),
  });

  return (
    <div>
      {question.input_type === "textarea" ? (
        <Textarea {...common} placeholder={textPlaceholder} />
      ) : (
        <Input
          {...common}
          type={type}
          placeholder={placeholder}
          inputMode={type === "number" ? "decimal" : undefined}
          invalid={Boolean(errorKey)}
          aria-describedby={errorKey ? errorId : undefined}
        />
      )}
      {errorKey && (
        <Text id={errorId} variant="caption" tone="error" className="mt-1">
          {t(`errors.${errorKey}`)}
        </Text>
      )}
    </div>
  );
}
